// lib/validation-schemas.js
import * as z from "zod";

// Base schema for all users
export const baseUserSchema = z.object({
  email: z.string().email("Invalid email address"),
  password: z.string()
    .min(8, "Password must be at least 8 characters")
    .regex(/[A-Z]/, "Password must contain at least one uppercase letter")
    .regex(/[a-z]/, "Password must contain at least one lowercase letter")
    .regex(/[0-9]/, "Password must contain at least one number"),
  termsAccepted: z.boolean().refine(val => val === true, {
    message: "You must accept the terms and conditions",
  }),
});

// Individual user schema
export const individualSchema = baseUserSchema.extend({
  userType: z.literal("individual"),
  name: z.string().min(2, "Name must be at least 2 characters"),
  phone: z.string().optional(),
  city: z.string().optional(),
  organizationName: z.string().optional(),
  organizationType: z.string().optional(),
  orgAddress: z.string().optional(),
  sendMe: z.enum(["both", "newsletter", "events", "none"]).default("both"),
});

// Expert schema
export const expertSchema = baseUserSchema.extend({
  userType: z.literal("expert"),
  firstName: z.string().min(2, "First name must be at least 2 characters"),
  lastName: z.string().min(2, "Last name must be at least 2 characters"),
  phone: z.string().min(10, "Phone number must be at least 10 digits"),
  gender: z.string().min(1, "Gender is required"),
  country: z.string().min(1, "Country is required"),
  city: z.string().min(1, "City is required"),
  highestDegree: z.string().min(1, "Highest degree is required"),
  fieldOfStudy: z.string().min(1, "Field of study is required"),
  expertise: z.array(z.string()).min(1, "Select at least one expertise area"),
  skills: z.array(z.string()).min(1, "Select at least one skill"),
  arabicProficiency: z.string().min(1, "Arabic proficiency is required"),
  englishProficiency: z.string().min(1, "English proficiency is required"),
  digitalSkills: z.string().min(1, "Digital skills level is required"),
  experienceYears: z.string().min(1, "Experience years is required"),
  dailyRate: z.string().min(1, "Daily rate is required"),
  currency: z.string().min(1, "Currency is required"),
  canTravel: z.string().min(1, "Travel ability is required"),
  travelDetails: z.string().optional(),
  specificSkills: z.string().optional(),
  specificExpertise: z.string().optional(),
  cv: z.any().optional(),
});

// Organization schema
export const organizationSchema = baseUserSchema.extend({
  userType: z.union([z.literal("employee"), z.literal("ngo")]),
  organizationName: z.string().min(2, "Organization name must be at least 2 characters"),
  position: z.string().min(2, "Position is required"),
  ceoName: z.string().optional(),
  phone: z.string().min(10, "Phone number is required"),
  country: z.string().min(1, "Country is required"),
  city: z.string().min(1, "City is required"),
  abbreviation: z.string().optional(),
  otherEmail: z.string().email("Invalid email address").optional().or(z.literal("")),
  description: z.string().optional(),
  website: z.string().url("Invalid URL").optional().or(z.literal("")),
  foundingYear: z.string().optional(),
  staffSize: z.string().optional(),
  sectors: z.array(z.string()).optional(),
  countriesOfOperation: z.array(z.string()).optional(),
  profileDocument: z.any().optional(),
});

// Form field types
export const formDataTypes = {
  education: z.array(z.object({
    id: z.number(),
    name_en: z.string(),
    name_ar: z.string(),
  })),
  educationMaj: z.array(z.object({
    id: z.number(),
    name_en: z.string(),
    name_ar: z.string(),
  })),
  Gender: z.array(z.object({
    id: z.number(),
    name_en: z.string(),
    name_ar: z.string(),
  })),
  Counties: z.array(z.object({
    id: z.number(),
    name_en: z.string(),
    name_ar: z.string(),
  })),
  Cities: z.array(z.object({
    id: z.number(),
    name_en: z.string(),
    name_ar: z.string(),
  })),
  ExpField: z.array(z.object({
    id: z.number(),
    name_en: z.string(),
    name_ar: z.string(),
  })),
  WorkExp: z.array(z.object({
    id: z.number(),
    name_en: z.string(),
    name_ar: z.string(),
  })),
  WorkSkills: z.array(z.object({
    id: z.number(),
    name_en: z.string(),
    name_ar: z.string(),
  })),
  Proficiency: z.array(z.object({
    id: z.number(),
    name_en: z.string(),
    name_ar: z.string(),
  })),
  Currincies: z.array(z.object({
    id: z.number(),
    name_en: z.string(),
    name_ar: z.string(),
  })),
  Travel: z.array(z.object({
    id: z.number(),
    name_en: z.string(),
    name_ar: z.string(),
  })),
};